import { useEffect, useRef, useState } from 'react';
import { FieldHelp } from '../ui/FieldHelp';
import { FormCollapsePanel } from '../ui/FormCollapsePanel';
import { SupportRoutingRulesPatternList } from './SupportRoutingRulesPatternList';
import {
  emptySupportRoutingRulesFormState,
  parseSupportRoutingRulesJson,
  serializeSupportRoutingRulesJson,
  SupportRoutingRulesFormState,
} from '../../utils/supportRoutingRulesJson';

type SupportRoutingRulesFieldProps = {
  label: string;
  description?: string;
  helpDescription?: string;
  helpExample?: string;
  value?: string | null;
  onChange: (json: string) => void;
  disabled?: boolean;
};

const AFTER_HOURS_OPTIONS: Array<{
  value: SupportRoutingRulesFormState['afterHoursAction'];
  label: string;
}> = [
  { value: 'Process', label: 'Processar normalmente' },
  { value: 'AutoReply', label: 'Resposta automática fora do horário' },
  { value: 'Defer', label: 'Adiar até o horário comercial' },
];

export function SupportRoutingRulesField({
  label,
  description,
  helpDescription,
  helpExample,
  value,
  onChange,
  disabled = false,
}: SupportRoutingRulesFieldProps) {
  const [state, setState] = useState<SupportRoutingRulesFormState>(() =>
    parseSupportRoutingRulesJson(value),
  );
  const [advancedJson, setAdvancedJson] = useState(() => {
    const extras = parseSupportRoutingRulesJson(value).extras;
    return Object.keys(extras).length > 0 ? JSON.stringify(extras, null, 2) : '';
  });
  const [advancedError, setAdvancedError] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(
    () => Object.keys(parseSupportRoutingRulesJson(value).extras).length > 0,
  );
  const lastEmittedRef = useRef(value?.trim() ?? '');

  useEffect(() => {
    const normalized = value?.trim() ?? '';
    if (normalized === lastEmittedRef.current) {
      return;
    }
    lastEmittedRef.current = normalized;
    const next = parseSupportRoutingRulesJson(value);
    setState(next);
    setAdvancedJson(
      Object.keys(next.extras).length > 0 ? JSON.stringify(next.extras, null, 2) : '',
    );
    setAdvancedError(null);
    if (Object.keys(next.extras).length > 0) {
      setAdvancedOpen(true);
    }
  }, [value]);

  function emit(next: SupportRoutingRulesFormState): void {
    setState(next);
    const json = serializeSupportRoutingRulesJson(next);
    lastEmittedRef.current = json;
    onChange(json);
  }

  function patch(partial: Partial<SupportRoutingRulesFormState>): void {
    emit({ ...state, ...partial });
  }

  function applyAdvancedJson(): void {
    const trimmed = advancedJson.trim();
    if (!trimmed) {
      setAdvancedError(null);
      patch({ extras: {} });
      return;
    }

    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setAdvancedError('Informe um objeto JSON (ex.: { "ignoreContent": [] }).');
        return;
      }
      setAdvancedError(null);
      patch({ extras: parsed as Record<string, unknown> });
    } catch {
      setAdvancedError('JSON inválido.');
    }
  }

  const hasRules =
    state.ignoreAddresses.some((item) => item.trim())
    || state.doNotReplyAddresses.some((item) => item.trim())
    || state.alertGroupAddresses.some((item) => item.trim())
    || state.afterHoursAction !== 'Process'
    || state.afterHoursAutoReplyMessage.trim().length > 0
    || state.businessHoursCalendarId.trim().length > 0
    || Object.keys(state.extras).length > 0;

  return (
    <div className="rounded-xl border border-secondary-200 bg-secondary-50/40 p-3">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-sm font-medium text-secondary-900">
            <span>{label}</span>
            {helpDescription ? (
              <FieldHelp description={helpDescription} example={helpExample} />
            ) : null}
          </div>
          {description ? (
            <p className="mt-0.5 text-xs text-secondary-500">{description}</p>
          ) : null}
          {!hasRules ? (
            <p className="mt-1 text-xs text-secondary-600">Nenhuma regra definida.</p>
          ) : null}
        </div>
        {hasRules ? (
          <button
            type="button"
            className="erp-btn-secondary erp-btn-sm"
            disabled={disabled}
            onClick={() => {
              emit(emptySupportRoutingRulesFormState());
              setAdvancedJson('');
              setAdvancedError(null);
            }}
          >
            Limpar regras
          </button>
        ) : null}
      </div>

      <div className="space-y-3">
        <SupportRoutingRulesPatternList
          label="Descartar remetentes"
          description="Chave ignoreAddresses — a mensagem é ignorada (não abre chamado). Ex.: @eveo.com.br ou spam@"
          values={state.ignoreAddresses}
          placeholder="@dominio.com.br ou trecho@…"
          disabled={disabled}
          onChange={(ignoreAddresses) => patch({ ignoreAddresses })}
        />

        <SupportRoutingRulesPatternList
          label="Abrir sem responder"
          description="Chave doNotReplyAddresses — abre/atualiza o chamado, mas bloqueia N1, ack e auto-reply."
          values={state.doNotReplyAddresses}
          placeholder="noreply@ ou @dominio.com.br"
          disabled={disabled}
          onChange={(doNotReplyAddresses) => patch({ doNotReplyAddresses })}
        />

        <SupportRoutingRulesPatternList
          label="Agrupar alertas"
          description="Chave alertGroupAddresses — remetentes elegíveis ao agrupamento (vazio = usa “Abrir sem responder”)."
          values={state.alertGroupAddresses}
          placeholder="btalk-suporte@ ou @dominio.com.br"
          disabled={disabled}
          onChange={(alertGroupAddresses) => patch({ alertGroupAddresses })}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-secondary-600">
              Janela de agrupamento (horas)
            </span>
            <input
              type="number"
              min={1}
              max={168}
              className="erp-input-compact w-full"
              value={state.alertGroupWindowHours}
              disabled={disabled}
              onChange={(event) =>
                patch({ alertGroupWindowHours: Number(event.target.value) || 24 })
              }
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-secondary-600">
              Fora do horário comercial
            </span>
            <select
              className="erp-input-compact w-full"
              value={state.afterHoursAction}
              disabled={disabled}
              onChange={(event) =>
                patch({
                  afterHoursAction: event.target
                    .value as SupportRoutingRulesFormState['afterHoursAction'],
                })
              }
            >
              {AFTER_HOURS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {state.afterHoursAction === 'AutoReply' ? (
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-secondary-600">
              Mensagem de auto-reply fora do horário
            </span>
            <textarea
              className="erp-input-compact min-h-[4.5rem] w-full"
              value={state.afterHoursAutoReplyMessage}
              disabled={disabled}
              placeholder="Recebemos sua mensagem fora do horário comercial…"
              onChange={(event) => patch({ afterHoursAutoReplyMessage: event.target.value })}
            />
          </label>
        ) : null}

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-secondary-600">
            Calendário de horário comercial (opcional)
          </span>
          <input
            className="erp-input-compact w-full"
            value={state.businessHoursCalendarId}
            disabled={disabled}
            placeholder="GUID do calendário"
            onChange={(event) => patch({ businessHoursCalendarId: event.target.value })}
          />
        </label>

        <FormCollapsePanel
          title="Regras avançadas (JSON)"
          summary="ignoreContent, keywordCatalogRules e outros campos livres"
          expanded={advancedOpen}
          onToggle={() => setAdvancedOpen((current) => !current)}
        >
          <p className="mb-2 text-xs text-secondary-500">
            Use para <code className="text-[11px]">ignoreContent</code> e{' '}
            <code className="text-[11px]">keywordCatalogRules</code>. O restante do formulário
            acima é preservado ao aplicar.
          </p>
          <textarea
            className="erp-input-compact min-h-[7rem] w-full font-mono text-xs"
            value={advancedJson}
            disabled={disabled}
            placeholder={`{\n  "keywordCatalogRules": [{ "keywords": ["reset"], "catalogItemId": "00000000-0000-0000-0000-000000000000", "matchIn": "Subject" }]\n}`}
            onChange={(event) => setAdvancedJson(event.target.value)}
            onBlur={() => applyAdvancedJson()}
          />
          {advancedError ? (
            <p className="mt-1 text-xs text-red-600">{advancedError}</p>
          ) : null}
          <button
            type="button"
            className="erp-btn-secondary erp-btn-sm mt-2"
            disabled={disabled}
            onClick={() => applyAdvancedJson()}
          >
            Aplicar JSON avançado
          </button>
        </FormCollapsePanel>
      </div>
    </div>
  );
}
